import { inject, InjectionToken } from '@angular/core';
import { oauthProviderClient } from '@better-auth/oauth-provider/client';
import { ProblemDetails } from '@easihub/esm';
import type { BetterAuthServerType } from '@easihub/server-side/auth';
import { createAuthClient, kBroadcastChannel, type Atom, type BroadcastChannel as BetterAuthBroadcastChannel } from 'better-auth/client';
import { adminClient, anonymousClient, emailOTPClient, inferAdditionalFields, usernameClient } from 'better-auth/client/plugins';
import { CLIENT_CONFIG } from '../dependency-injection';
import { isServer } from '../utilities/platform-type';
import { customEasAuthClientPlugin } from './better-auth.custom-plugin';

export type BetterAuthClientType = typeof BETTER_AUTH_CLIENT extends InjectionToken<infer T> ? T : never;

type AtomValue<A> = A extends Atom<infer V> ? V : never;
export type SessionAtomValue = AtomValue<BetterAuthClientType['useSession']>;
export type SessionAtomData = SessionAtomValue['data'];
export type BetterAuthClientSession = NonNullable<SessionAtomData>['session'];
export type BetterAuthClientUser = NonNullable<SessionAtomData>['user'];

/** better-auth's own cross-tab sync, made inert — the session monitor is the sole mechanism */
const INERT_BROADCAST_CHANNEL: BetterAuthBroadcastChannel = {
  post: () => void 0,
  subscribe: () => () => void 0,
  setup: () => () => void 0,
};

/** the real one is created lazily on first use, so this only works before the session atom mounts */
function disableBetterAuthBroadcast() {
  const globals = globalThis as unknown as Record<symbol, unknown>;

  if (globals[kBroadcastChannel])
    console.error('[auth] better-auth broadcast channel was already created — cross-tab events will fire twice');

  globals[kBroadcastChannel] = INERT_BROADCAST_CHANNEL;
}

export const BETTER_AUTH_CLIENT = new InjectionToken('BETTER_AUTH_CLIENT', {
  providedIn: 'root',
  factory: () => {
    if (isServer())
      throw new Error('cannot initialize BETTER_AUTH_CLIENT on the server');

    const clientConfig = inject(CLIENT_CONFIG);

    disableBetterAuthBroadcast();

    return createAuthClient({
      sessionOptions: {
        refetchOnWindowFocus: false
      },
      baseURL: `${clientConfig.api}/auth`,
      plugins: [
        inferAdditionalFields<BetterAuthServerType>(),
        adminClient(),
        anonymousClient(),
        emailOTPClient(),
        usernameClient(),
        oauthProviderClient(),
        customEasAuthClientPlugin()
      ],
    });
  }
});

type BetterAuthResult<T> =
  | { data: T; error: null; }
  | { data: null; error: { code?: string; message?: string;[key: string]: unknown; }; };

export function processBetterAuthResult<T>(result: BetterAuthResult<T>) {
  if (result.error) {
    const err = ProblemDetails.fromObject(result.error);

    // normal errors carry `code`; oauth-provider errors carry `error` (the OAuth
    // error code, e.g. invalid_signature)
    const code = result.error.code ?? result.error['error'];
    if (typeof code === 'string')
      err.type = `better-auth:${code}`;

    if (typeof result.error['status'] === 'number')
      err.status = result.error['status'];

    throw err;
  }

  return result.data;
}